import { extendTheme } from "@chakra-ui/react";
import { CardComponent } from "./additions/card/card";
import { buttonStyles } from "./components/button";
import { badgeStyles } from "./components/badge";
import { inputStyles } from "./components/input";
import { progressStyles } from "./components/progress";
import { sliderStyles } from "./components/slider";
import { textareaStyles } from "./components/textarea";
import { switchStyles } from "./components/switch";
import { linkStyles } from "./components/link";
import { breakpoints } from "./foundations/breakpoints";
import { globalStyles } from "./styles";

// Modo noche: lo cambia el botón de la barra superior y Chakra lo recuerda en el navegador.
export const config = { initialColorMode: "light", useSystemColorMode: false };

// Los grises con que la app escribe textos secundarios (etiquetas, ayudas, tablas). En modo
// noche todo el texto va en blanco: en vez de cambiar cientos de `color="gray.500"`, estos
// tonos valen blanco en modo noche. En modo claro quedan como siempre. Se usan casi solo como
// color de texto; donde Chakra los usa de fondo en modo noche (skeleton) se corrige abajo.
const GRISES_DE_TEXTO = {
  gray: { 300: "#CBD5E0", 400: "#A0AEC0", 500: "#718096", 600: "#4A5568" },
  secondaryGray: { 500: "#8F9BBA", 600: "#A3AED0", 700: "#707EAE", 800: "#707EAE", 900: "#1B2559" },
};

const blancosEnModoNoche = Object.fromEntries(Object.entries(GRISES_DE_TEXTO).map(([paleta, tonos]) => [
  paleta,
  Object.fromEntries(Object.entries(tonos).map(([tono, valor]) => [tono, { default: valor, _dark: "white" }])),
]));

// Superficies que cambian solas con el modo noche. Úsalas en vez de "white" o "gray.50":
// un fondo blanco fijo queda como un bloque encendido sobre el fondo oscuro.
export const semanticTokens = {
  colors: {
    ...blancosEnModoNoche,
    superficie: { default: "white", _dark: "navy.800" },
    superficieSuave: { default: "gray.50", _dark: "whiteAlpha.50" },
    superficieResaltada: { default: "gray.100", _dark: "whiteAlpha.100" },
    superficieMarca: { default: "brand.50", _dark: "whiteAlpha.100" },
    superficieInfo: { default: "blue.50", _dark: "rgba(144, 205, 244, 0.12)" },
    superficiePeligro: { default: "red.50", _dark: "rgba(254, 178, 178, 0.12)" },
    borde: { default: "gray.200", _dark: "whiteAlpha.200" },
    textoSuave: { default: "gray.600", _dark: "white" },
    // Textos oscuros fijos (títulos, valores de campos) que en modo noche no se verían.
    textoFuerte: { default: "#2D3748", _dark: "white" },
    textoPrincipal: { default: "black", _dark: "white" },
    // Los placeholders siguen un poco más tenues que el texto: así se distingue un campo vacío.
    textoPlaceholder: { default: "#A3AED0", _dark: "whiteAlpha.700" },
    "chakra-placeholder-color": { _light: "#718096", _dark: "whiteAlpha.700" },
  },
};

// En modo noche Chakra pinta el texto de los botones de borde, sin fondo y de enlace con el
// tono 200 del esquema: con el color de la empresa queda un lila que se lee gris. Van en
// blanco, salvo los esquemas con significado (eliminar en rojo, confirmar en verde...).
const ESQUEMAS_CON_SIGNIFICADO = new Set(["red", "orange", "yellow", "green"]);
const textoBlancoEnNoche = (props) => (ESQUEMAS_CON_SIGNIFICADO.has(props.colorScheme) ? {} : { _dark: { color: "white" } });

// Componentes de Chakra que en modo noche usaban esos grises de fondo o un gris propio.
const ajustesModoNoche = {
  components: {
    Button: { variants: { outline: textoBlancoEnNoche, ghost: textoBlancoEnNoche, link: textoBlancoEnNoche } },
    // Alternaba entre gray.800 y gray.600, que ahora es blanco: destellaba.
    Skeleton: { baseStyle: { _dark: { "--skeleton-end-color": "colors.whiteAlpha.300" } } },
    // Las ayudas debajo de los campos usaban un blanco translúcido (gris).
    Form: { baseStyle: { helperText: { _dark: { color: "white" } } } },
  },
};

export default extendTheme(
  { config, semanticTokens },
  ajustesModoNoche,
  { breakpoints }, // Breakpoints
  globalStyles,
  badgeStyles, // badge styles
  buttonStyles, // button styles
  linkStyles, // link styles
  progressStyles, // progress styles
  sliderStyles, // slider styles
  inputStyles, // input styles
  textareaStyles, // textarea styles
  switchStyles, // switch styles
  CardComponent // card component
);
